// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import XngLoadingOverlay from "@flip/App/Components/Common/XngLoadingOverlay";
import XngUtilities from "@flip/App/Components/Common/XngUtilities";
import XngZone from "@flip/App/Components/Common/XngZone";
import { xHeaderUpdate } from "@flip/App/Handlers/XNG/Header";
import useSize from "@flip/App/Hooks/useSize";
import { CustomAppState } from "@flip/App/Store/Reducers";
import { xIsAuthenticated } from "@flip/App/Store/Selectors/GeneralSelectors";
import { FormFieldClickable } from "@flip/MobiusOverrides/FormField/FormField";
import { checkoutPages, constants, DivWrapper } from "@flip/XDL/constants";
import { PlaceholderSelector, placeholderStyle } from "@flip/XDL/PreStyle/formField";
import { color, layoutContainer, xScreen } from "@flip/XDL/Utilities";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { FormInputWrapper } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { InputStyle } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import Transition, { TransitionStatus } from "react-transition-group/Transition";
import styled, { css, ThemeProps } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    // XNG-Changes
    isTabletScreen: state.xCommon.isTabletScreen,
    registerMenu: state.xHeader.registerMenu,
    currentLocation: getLocation(state),
    navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
    isSticky: state.xHeader.isSticky,
    isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    isHomePage: getCurrentPage(state).type === "HomePage",
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    // XNG-Changes
    xHeaderUpdate,
    setNavDrawerIsOpen,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // XNG-Changes
    wrapper?: InjectableCss;
    mainNaviationContainer?: InjectableCss;
    headerActions?: InjectableCss;
    searchWrapper?: InjectableCss;
    searchContainer?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        background: "common.ui1",
        css: css`
            position: relative;
            z-index: ${getProp("theme.zIndex.header")};
            &&&& {
                ${xScreen("lte", "md")} {
                    position: sticky;
                    top: 0;
                }
            }
        `,
    },
    wrapper: {
        css: css`
            background-color: ${getColor("common.ui1")};
            ${({ isCheckoutPage }: any) => css`
                ${isCheckoutPage
                    ? css`
                          &&& {
                              border-bottom: 1px solid ${color("common.ui3")};
                          }
                      `
                    : css`
                          &&& {
                              border-bottom: 1px solid ${color("common.ui5")};
                          }
                      `};
            `}
        `,
    },
    container: {
        css: css`
            display: flex;
            padding-top: 12px;
            padding-bottom: 12px;

            ${xScreen("lt", "sm")} {
                &&& {
                    padding-top: 8px;
                    padding-bottom: 8px;
                    padding-right: 10px;
                }
            }

            ${layoutContainer}
        `,
    },
    // XNG-Changes: FLIP-975
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    mainNaviationContainer: {
        css: css`
            display: flex;
            align-items: center;
            flex: 1;
            padding: 0 67px;

            ${xScreen("lt", 1300)} {
                padding: 0 32px;
            }

            ${xScreen("lte", "md")} {
                order: -1;
                padding: 0;
                margin-right: 10px;
                flex: 0;
            }
        `,
    },
    headerActions: {
        css: css`
            > ${DivWrapper} {
                display: flex;
                align-items: center;
                flex-direction: row;
                flex-wrap: nowrap;
                height: 100%;
                > :first-child {
                    margin-right: 32px;
                    ${xScreen("lte", "md")} {
                        margin-right: 20px;
                    }
                }
            }
            ${xScreen("lte", "md")} {
                margin-left: auto;
            }
        `,
    },
    searchWrapper: {
        css: css`
            background-color: ${color("primary")};
            padding-top: 12px;
            padding-bottom: 12px;

            ${FormInputWrapper} {
                ${InputStyle} {
                    height: 37px;
                    line-height: 16px;

                    ${placeholderStyle}
                }

                ${FormFieldClickable},${IconWrapper} {
                    height: 37px;
                }
            }
            ${xScreen("lte", "md")} {
                padding-top: 4px;
                padding-bottom: 4px;
            }
        `,
    },
    searchContainer: {
        css: css`
            ${layoutContainer};
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    // XNG-Changes
    isTabletScreen,
    registerMenu,
    currentLocation,
    navDrawerIsOpen,
    isSticky,
    isHomePage,
    isCheckoutPage,
    isAuthenticated,
    xHeaderUpdate,
    setNavDrawerIsOpen,
}: Props) => {
    // <XNG-Changes>
    const headerRef = React.useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef)?.height;

    // XNG-Changes: Hide main navigation
    useEffect(() => {
        setTimeout(() => {
            hideAll();
        });
        if (navDrawerIsOpen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
            setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
        }
    }, [isSticky, currentLocation]);

    useEffect(() => {
        if (headerHeight) {
            xHeaderUpdate({ key: "headerHeight", value: `${headerHeight}px` });
        }
    }, [headerHeight]);
    // </XNG-Changes>

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler} aria-label="View My Dashboard">
                            {translate("View My Dashboard")}
                        </Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {/* // XNG-Changes: FLIP-975 */}
            {/* {siteName && <Typography {...styles.websiteName}>{siteName}</Typography>} */}
            <Page as="header" {...styles.page} {...{ isCheckoutPage }}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {!isTabletScreen && <XngZone contentId={id} zoneName="UtilityBar" hint="Utility Bar" />}
                    <StyledWrapper {...styles.wrapper} ref={headerRef} {...{ isCheckoutPage }}>
                        <StyledWrapper id="header-container" {...styles.container}>
                            <StyledWrapper id="header-logo">
                                <XngZone contentId={id} zoneName="Logo" hint="Zone for Logo" />
                            </StyledWrapper>
                            {!isCheckoutPage && (
                                <>
                                    <StyledWrapper {...styles.mainNaviationContainer}>
                                        <XngZone
                                            contentId={id}
                                            zoneName="MainNaviagtion"
                                            hint="Zone for Main navigation"
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.headerActions}>
                                        <XngZone
                                            contentId={id}
                                            zoneName="Actions"
                                            hint="Zone for [SignIn - Mini Cart - My Account flyout"
                                        />
                                    </StyledWrapper>
                                </>
                            )}
                        </StyledWrapper>
                        {(!isHomePage || isAuthenticated) && !isCheckoutPage && (
                            <StyledWrapper {...styles.searchWrapper}>
                                <StyledWrapper {...styles.searchContainer}>
                                    {createWidgetElement("Header/HeaderSearchInput", { fields: { id } })}
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    {!isCheckoutPage && <Zone contentId={id} zoneName="Breadcrumb" />}
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
                {/* <XNG-Changes> */}
                <XngUtilities />
                {/* <XngLoadingOverlay /> */}
                {/* </XNG-Changes> */}
            </Page>
            {/* <XNG-Changes> */}
            <Transition
                mountOnEnter
                unmountOnExit
                in={registerMenu && registerMenu.length > 0}
                timeout={{ enter: 75, exit: 250 }}
            >
                {state => (
                    <StyledOverlay
                        className={registerMenu && isTabletScreen ? "header-overlay" : ""}
                        transitionState={state}
                    />
                )}
            </Transition>
            {/* </XNG-Changes> */}
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;

// XNG-Changes
const StyledOverlay = styled.div<{ transitionState: TransitionStatus } & ThemeProps<BaseTheme>>`
    position: fixed;
    right: 0;
    bottom: 0;
    top: 0;
    left: 0;
    background-color: ${color("common.ui7", 0.3)};
    z-index: ${({ theme }: any) => {
        return theme.zIndex.styledOverlay;
    }};
    transition: opacity ease-in-out 250ms;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "opacity: 0;";
            case "entered":
                return "opacity: 1;";
            case "exiting":
            case "exited":
                return "opacity: 0;";
            default:
                return "";
        }
    }}
`;
